import { useMemo } from "react";
import { PropTypes } from "prop-types";
import { useIntl } from "react-intl";
import {
  ReactTable,
  reactTableSortCaseInsensitive,
  IndeterminateCheckboxHeader,
  IndeterminateCheckboxCell
} from "@bafsllc/ui-shared";
import Constants from "../../../../../../../../../../services/Constants/strings";

import ActionsCell from "./Cells/ActionsCell";
import DescriptionCell from "./Cells/DescriptionCell";
import MarketValueCell from "./Cells/MarketValueCell";
import PrettyDateCell from "./Cells/PrettyDateCell";
import BorrowerEditStatusCell from "./Cells/BorrowerEditStatusCell";

function AssetTable({
  assets,
  isFetching = false,
  showActionsCell = false,
  showActionsMenu = false,
  showBorrowerStatusCell = false,
  showSelectCell = false,
  onSelectionChange,
  // ActionCell action handlers
  onClickEdit,
  onClickBorrowerEditLock
}) {
  const { formatMessage } = useIntl();
  const hiddenColumns = useMemo(() => {
    const cols = ["institution_asset_id"];

    if (!showActionsCell) {
      cols.push("actionsCell");
    }

    if (!showBorrowerStatusCell) {
      cols.push("borrowerStatusCell");
    }

    if (!showSelectCell) {
      cols.push("select");
    }

    return cols;
  }, [showActionsCell, showBorrowerStatusCell, showSelectCell]);

  const isRowSelectable = row => {
    const asset = row?.original;
    const editableByBorrower = asset?.bpAccessControls?.editable_by_borrower;

    return editableByBorrower;
  };

  const handleHooks = hooks => {
    if (!hiddenColumns.includes("select")) {
      hooks.visibleColumns.push(visibleColumns => [
        {
          id: "selection",
          sticky: "left",
          width: 40,
          Header: props => {
            return (
              <IndeterminateCheckboxHeader
                {...props}
                isRowSelectable={isRowSelectable}
              />
            );
          },
          Cell: props => {
            const disabled =
              !props.cell.row.original?.bpAccessControls?.editable_by_borrower;
            return <IndeterminateCheckboxCell {...props} disabled={disabled} />;
          }
        },
        ...visibleColumns
      ]);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: Constants.DESCRIPTION,
        accessor: "description",
        sticky: "left",
        width: 250,
        sortType: reactTableSortCaseInsensitive,
        Cell: DescriptionCell
      },
      {
        Header: Constants.GROUP,
        accessor: "asset_group",
        sortType: reactTableSortCaseInsensitive
      },
      {
        Header: Constants.CLASS,
        accessor: "asset_class",
        sortType: reactTableSortCaseInsensitive
      },
      {
        Header: Constants.TYPE,
        accessor: "asset_type",
        width: 200,
        sortType: reactTableSortCaseInsensitive
      },
      {
        Header: Constants.VALUE,
        accessor: "market_value",
        sortType: reactTableSortCaseInsensitive,
        Cell: MarketValueCell
      },
      {
        Header: Constants.ACQUISITION_DATE,
        accessor: "acquisition_date",
        sortType: reactTableSortCaseInsensitive,
        Cell: PrettyDateCell
      },
      {
        Header: Constants.BORROWER_EDIT_STATUS,
        accessor: "borrowerStatusCell",
        width: 180,
        disableSortBy: true,
        Cell: BorrowerEditStatusCell
      },
      {
        Header: "",
        accessor: "actionsCell",
        sticky: "right",
        width: 80,
        disableSortBy: true,
        Cell: ActionsCell,
        showActionsMenu,
        onClickEdit,
        onClickBorrowerEditLock,
        onSelectionChange
      }
    ],
    [onClickBorrowerEditLock, onClickEdit, onSelectionChange, showActionsMenu]
  );

  return (
    <ReactTable
      columns={columns}
      data={assets}
      isFetching={isFetching}
      emptyDataTitle={formatMessage({ id: "ASSETS_NO_DATA_TITLE" })}
      emptyDataSubtitle={formatMessage({ id: "ASSETS_NO_DATA_SUBTITLE" })}
      handleHooks={handleHooks}
      hiddenColumns={hiddenColumns}
      onSelectionChange={onSelectionChange}
      sortByAccessorId="description"
      sortByAccessorDesc={false}
    />
  );
}

export default AssetTable;

AssetTable.propTypes = {
  assets: PropTypes.arrayOf(PropTypes.shape()),
  showActionsCell: PropTypes.bool,
  showSelectCell: PropTypes.bool,
  showBorrowerStatusCell: PropTypes.bool,
  showActionsMenu: PropTypes.bool,
  onClickEdit: PropTypes.func,
  onClickBorrowerEditLock: PropTypes.func,
  onSelectionChange: PropTypes.func
};
